
import { Component, Vue } from 'vue-property-decorator';

import SelectField from '../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../framework/state';
import { getEnumerationOptions, getOptionLabel } from '../../../../framework/service/options';
import {
    deleteResource,
    getResourcePagingNew,
    getResourcesNew,
    putResource,
} from '../../../../framework/client/resource';

import { VendorContract } from '../../../../../common/application/model/VendorContract';
import { VendorContractType } from '../../../../../common/application/model/enums/VendorContractType';
import { Customer } from '../../../../../common/application/model/Customer';

import { errorToast, successToast } from '../../../service/toast_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { VehicleCostMonthlyReport } from '../../../../../common/application/model/VehicleCostMonthlyReport';
import { getCustomerOptions } from '../../../service/application_options_service';
import { formatCurrency, formatToFinnishDate } from '../../../service/helper_service';
import ParameterService from '../../../service/parameter_service';

@Component({
    components: {
        SelectField,
    },
})
export default class VehicleCostMonthlyReports extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_COST_MONTHLY_REPORT;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as VehicleCostMonthlyReport[],
        customer: undefined as Customer | undefined,
        total: 0,
        loading: false,
        selected: undefined as VehicleCostMonthlyReport | undefined,
        customerOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
        customerIds: [] as string[],
    };

    async mounted() {
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        await this.loadAsyncData();
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters, undefined)).rowCount;

        this.local.rows = [];

        const rows = await getResourcesNew<VehicleCostMonthlyReport>(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async markAsHandled() {
        if (this.local.selected) {
            try {
                await putResource<VehicleCostMonthlyReport>(this.resourceType, this.local.selected.id, {
                    ...this.local.selected,
                    handled: true,
                });
                successToast(this, 'message.resourceUpdated');
                await this.loadAsyncData();
            } catch (e) {
                errorToast(this);
            }
        }
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    formatDate(date: string): string {
        return formatToFinnishDate(date);
    }

    onRowClick(row: VendorContract) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
